/**
 * 旋转矩形几何：R1 轮廓内判定、R2 点是否在孔内。
 *
 * 约定：canvas 坐标；角度为 rotationRad（绕中心逆时针）。
 */

import { isPointInPolygon } from '../core.js';

/**
 * 将实例转为旋转矩形描述。
 * @param {Object} instance - 含 centerPoint、width、height、rotationRad
 * @returns {{cx:number,cy:number,w:number,h:number,rotationRad:number}}
 */
function instanceToRect(instance) {
  return {
    cx: instance.centerPoint.x,
    cy: instance.centerPoint.y,
    w: instance.width,
    h: instance.height,
    rotationRad: instance.rotationRad || 0
  };
}

/**
 * 旋转矩形四角（canvas 坐标）。
 * @param {{cx:number,cy:number,w:number,h:number,rotationRad:number}} rect
 * @returns {Array<{x:number,y:number}>}
 */
function getRectCorners(rect) {
  const cx = rect.cx;
  const cy = rect.cy;
  const w = rect.w;
  const h = rect.h;
  const cos = Math.cos(rect.rotationRad);
  const sin = Math.sin(rect.rotationRad);
  const local = [
    { x: -w / 2, y: -h / 2 },
    { x: w / 2, y: -h / 2 },
    { x: w / 2, y: h / 2 },
    { x: -w / 2, y: h / 2 }
  ];
  return local.map(function (p) {
    return {
      x: cx + p.x * cos - p.y * sin,
      y: cy + p.x * sin + p.y * cos
    };
  });
}

/**
 * R1 探测点：每条边含两端角点，边内再等分 samplesPerEdge 段（默认 2 → 四角 + 四边中点 = 8 点）。
 * @param {Object} instance - 底座孔实例
 * @param {number} [samplesPerEdge=2] - 每条边的分段数（≥1）
 * @returns {Array<{x:number,y:number}>}
 *
 * @example
 * getRotatedRectProbePoints({ centerPoint:{x:100,y:100}, width:40, height:20, rotationRad:0 }, 2)
 */
export function getRotatedRectProbePoints(instance, samplesPerEdge) {
  const n =
    typeof samplesPerEdge === 'number' && samplesPerEdge >= 1
      ? Math.floor(samplesPerEdge)
      : 2;
  const corners = getRectCorners(instanceToRect(instance));
  const probes = [];
  for (let i = 0; i < 4; i++) {
    const a = corners[i];
    const b = corners[(i + 1) % 4];
    probes.push(a);
    for (let s = 1; s < n; s++) {
      const t = s / n;
      probes.push({
        x: a.x + (b.x - a.x) * t,
        y: a.y + (b.y - a.y) * t
      });
    }
  }
  return probes;
}

/**
 * R2：点是否在旋转矩形开区间内部（边界向内缩 epsilonPx，避免贴边浮点误报）。
 * @param {{x:number,y:number}} pt - canvas 点
 * @param {{cx:number,cy:number,w:number,h:number,rotationRad:number}} rect
 * @param {number} [epsilonPx=0.5]
 * @returns {boolean}
 *
 * @example
 * isPointInRotatedRectInterior({x:0,y:0}, {cx:0,cy:0,w:10,h:10,rotationRad:0}, 0.5) // true
 */
export function isPointInRotatedRectInterior(pt, rect, epsilonPx) {
  const eps = typeof epsilonPx === 'number' && epsilonPx >= 0 ? epsilonPx : 0.5;
  const dx = pt.x - rect.cx;
  const dy = pt.y - rect.cy;
  const cos = Math.cos(-rect.rotationRad);
  const sin = Math.sin(-rect.rotationRad);
  const lx = dx * cos - dy * sin;
  const ly = dx * sin + dy * cos;
  const halfW = rect.w / 2 - eps;
  const halfH = rect.h / 2 - eps;
  if (halfW <= 0 || halfH <= 0) return false;
  return lx > -halfW && lx < halfW && ly > -halfH && ly < halfH;
}

/**
 * R1：旋转矩形是否整体在刀线多边形内（全探测点 isPointInPolygon）。
 * @param {Object} instance - 底座孔实例
 * @param {Array<Array<number>>} outlinePoints - 刀线点 [[x,y],...]
 * @param {number} [samplesPerEdge=2]
 * @returns {boolean}
 */
export function isRotatedRectInsideOutline(
  instance,
  outlinePoints,
  samplesPerEdge
) {
  if (!outlinePoints || !outlinePoints.length) return true;
  const probes = getRotatedRectProbePoints(instance, samplesPerEdge);
  for (let i = 0; i < probes.length; i++) {
    if (!isPointInPolygon(probes[i], outlinePoints)) return false;
  }
  return true;
}

/**
 * 返回第一个不在轮廓内的 R1 探测点，供 clampSelf 推中心用。
 * @param {Object} instance
 * @param {Array<Array<number>>} outlinePoints
 * @param {number} [samplesPerEdge=2]
 * @returns {{x:number,y:number}|null}
 */
export function findFirstOutlineViolationProbe(
  instance,
  outlinePoints,
  samplesPerEdge
) {
  if (!outlinePoints || !outlinePoints.length) return null;
  const probes = getRotatedRectProbePoints(instance, samplesPerEdge);
  for (let i = 0; i < probes.length; i++) {
    if (!isPointInPolygon(probes[i], outlinePoints)) return probes[i];
  }
  return null;
}

/**
 * 从 instance 构造 rect 并判 R2（供 collision 调用）。
 * @param {{x:number,y:number}} pt
 * @param {Object} instance
 * @param {number} epsilonPx
 * @returns {boolean}
 */
export function isPointInBaseHoleInterior(pt, instance, epsilonPx) {
  return isPointInRotatedRectInterior(pt, instanceToRect(instance), epsilonPx);
}
